import { useState } from 'react';
import type { OrCase, RoomResource, RoomStay, Stage } from './useAscSchedule';
import { STAGE_ORDER, stageLabel, nextStage } from './useAscSchedule';

// ── Patient flow board ───────────────────────────────────────────────────────
// Four lanes (Pre-op → OR → PACU → Discharge). Bays render from the live room
// inventory with occupancy/cleaning from patient_room_stays; each in-flight
// case card carries a single primary action: advance to the next stage
// (room pick prompted when the destination has multiple open bays).

const LANE_KINDS: Record<Stage, RoomResource['resource_kind'] | null> = {
    preop: 'preop_bay', inor: 'or_suite', pacu: 'pacu_bay', discharge: null,
};

function elapsed(since: string | null): string {
    if (!since) return '';
    const mins = Math.max(0, Math.round((Date.now() - new Date(since).getTime()) / 60_000));
    return mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

export function FlowBoard({ cases, rooms, stays, onAdvance, onBlocked, advancing, onMarkReady, markingReady }: {
    cases: OrCase[];
    rooms: RoomResource[];
    stays: RoomStay[];
    onAdvance: (c: OrCase, toStage: Stage, toRoomResourceId?: string) => void;
    onBlocked: (message: string) => void;
    advancing: boolean;
    onMarkReady: (roomResourceId: string) => void;
    markingReady: boolean;
}) {
    const [picking, setPicking] = useState<{ c: OrCase; toStage: Stage } | null>(null);

    const stayByResource = new Map(stays.map((s) => [s.room_resource_id, s]));
    const caseByEncounter = new Map(cases.filter((c) => c.encounter_id != null).map((c) => [c.encounter_id, c]));

    const active = cases.filter((c) => (c.case_status ?? '') !== 'cancelled' && (c.schedule_status ?? '') !== 'cancelled');
    const byStage = (s: Stage) => active.filter((c) => (c.stage ?? 'preop') === s && (s === 'discharge' ? true : c.completed_at == null));

    const openBays = (kind: RoomResource['resource_kind']) =>
        rooms.filter((r) => r.resource_kind === kind && !stayByResource.has(r.resource_id));

    function requestAdvance(c: OrCase) {
        const to = nextStage(c.stage);
        if (!to) return;
        if (c.encounter_id == null) {
            onBlocked(`${c.patient_name ?? 'This patient'} must be checked in before clinical movement can begin.`);
            return;
        }
        const kind = LANE_KINDS[to];
        if (!kind) { onAdvance(c, to); return; }
        const bays = openBays(kind);
        if (bays.length === 1) { onAdvance(c, to, bays[0].resource_id); return; }
        if (bays.length === 0) {
            onBlocked(`No clean ${to === 'inor' ? 'operating room' : `${stageLabel(to)} bay`} is available. Complete turnover or release a resource first.`);
            return;
        }
        setPicking({ c, toStage: to });
    }

    return (
        <section>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {STAGE_ORDER.map((stage) => {
                    const kind = LANE_KINDS[stage];
                    const laneRooms = kind ? rooms.filter((r) => r.resource_kind === kind) : [];
                    const occupied = laneRooms.filter((r) => stayByResource.get(r.resource_id)?.status === 'occupied').length;
                    const laneCases = byStage(stage);
                    return (
                        <div key={stage} className={[
                            'rounded-lg border bg-pt-panel p-3',
                            stage === 'inor' && laneCases.length > 0 ? 'border-pt-cyan/50' : 'border-pt-border',
                        ].join(' ')}>
                            <div className="flex items-baseline justify-between mb-2.5">
                                <span className={['text-sm font-medium', stage === 'inor' && laneCases.length > 0 ? 'text-pt-cyan' : 'text-pt-text'].join(' ')}>
                                    {stageLabel(stage)}
                                </span>
                                <span className="text-xs text-pt-text-muted">
                                    {kind ? `${occupied}/${laneRooms.length} ${stage === 'inor' ? 'rooms' : 'bays'}` : `${laneCases.length} today`}
                                </span>
                            </div>

                            <div className="space-y-2">
                                {laneCases.map((c) => {
                                    const to = nextStage(c.stage);
                                    return (
                                        <div key={c.case_id} className={[
                                            'rounded-md border px-3 py-2.5',
                                            stage === 'inor' ? 'border-pt-cyan/50 bg-pt-cyan/5' : 'border-pt-border bg-pt-darker',
                                        ].join(' ')}>
                                            <div className="text-sm font-medium text-pt-text truncate">{c.patient_name ?? `Case #${c.case_id}`}</div>
                                            <div className="text-[11px] text-pt-text-muted truncate mt-0.5">
                                                {c.procedure_name ?? 'Procedure TBD'}{c.or_room ? ` · ${c.or_room}` : ''}
                                            </div>
                                            {stage !== 'discharge' && (
                                                <div className="text-[11px] text-pt-text-dim mt-0.5">
                                                    In {stageLabel(stage as Stage).toLowerCase()} {elapsed(c.stage_entered_at ?? c.actual_start)}
                                                </div>
                                            )}
                                            {to && c.encounter_id != null && (
                                                <div className="mt-2 text-right">
                                                    <button
                                                        type="button"
                                                        disabled={advancing}
                                                        onClick={() => requestAdvance(c)}
                                                        className="text-[11px] font-medium rounded px-2.5 py-1 bg-emerald-500/90 text-pt-darker hover:bg-emerald-400 disabled:opacity-50 transition-colors"
                                                    >
                                                        Advance → {stageLabel(to)}
                                                    </button>
                                                </div>
                                            )}
                                            {to && c.encounter_id == null && (
                                                <div className="mt-2 text-right text-[10px] text-pt-text-dim">Awaiting check-in</div>
                                            )}
                                            {stage === 'discharge' && (
                                                <div className="mt-2 text-right text-[10px] text-emerald-400">Room released · discharge workflow</div>
                                            )}
                                        </div>
                                    );
                                })}

                                {kind && laneRooms.map((r) => {
                                    const stay = stayByResource.get(r.resource_id);
                                    if (stay?.status === 'occupied') {
                                        const occ = stay.encounter_id != null ? caseByEncounter.get(stay.encounter_id) : undefined;
                                        if (occ && (occ.stage ?? '') === stage) return null; // already shown as a case card
                                        return (
                                            <div key={r.resource_id} className="rounded-md border border-pt-border px-3 py-2 text-[11px] text-pt-text-muted">
                                                {r.display_name} · occupied
                                            </div>
                                        );
                                    }
                                    if (stay?.status === 'cleaning') {
                                        return (
                                            <div key={r.resource_id} className="rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 flex items-center justify-between gap-2">
                                                <span className="text-[11px] text-amber-400/90">{r.display_name} · turnover</span>
                                                <button
                                                    type="button"
                                                    disabled={markingReady}
                                                    onClick={() => onMarkReady(r.resource_id)}
                                                    className="text-[10px] font-medium rounded px-2 py-0.5 border border-amber-400/50 text-amber-300 hover:bg-amber-400/10 disabled:opacity-50 transition-colors"
                                                >
                                                    Mark ready
                                                </button>
                                            </div>
                                        );
                                    }
                                    return (
                                        <div key={r.resource_id} className="rounded-md border border-dashed border-pt-border px-3 py-2 text-[11px] text-pt-text-muted text-center">
                                            {r.display_name} · open
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            {picking && (
                <div className="mt-3 rounded-lg border border-pt-cyan/50 bg-pt-panel p-4 flex items-center gap-3 flex-wrap">
                    <span className="text-sm text-pt-text">
                        Move <span className="font-medium">{picking.c.patient_name}</span> to which {stageLabel(picking.toStage).toLowerCase()} {picking.toStage === 'inor' ? 'room' : 'bay'}?
                    </span>
                    {openBays(LANE_KINDS[picking.toStage]!).map((r) => (
                        <button key={r.resource_id} type="button"
                            onClick={() => { onAdvance(picking.c, picking.toStage, r.resource_id); setPicking(null); }}
                            className="text-xs rounded border border-pt-cyan/60 text-pt-cyan px-3 py-1.5 hover:bg-pt-cyan/10 transition-colors">
                            {r.display_name}
                        </button>
                    ))}
                    <button type="button" onClick={() => setPicking(null)} className="text-xs text-pt-text-muted px-2 py-1.5 hover:text-pt-text">Cancel</button>
                </div>
            )}
        </section>
    );
}
